import antlr4 from "antlr4";
import { default as sequenceLexer } from "../generated-parser/sequenceLexer";
import { default as sequenceParser } from "../generated-parser/sequenceParser";
import ToCollector from "./ToCollector";
import ChildFragmentDetector from "./ChildFragmentDetecotr";
import "./TitleContext";
import "./IsCurrent";
import "./Owner";
import "./ProgContext";
import "./RetContext";
import "./RefContext";
import "./Origin";
import "./Divider/DividerContext";
import "./SignatureText";
import "./Messages/MessageContext";
import "./From";
import "./utils/cloest-ancestor/ClosestAncestor";
import "./AncestorPath";
import { formatText } from "@/utils/StringUtil";

const errors = [];
const errorDetails = [];
class SeqErrorListener extends antlr4.error.ErrorListener {
  syntaxError(recognizer, offendingSymbol, line, column, msg) {
    errors.push(`${offendingSymbol} line ${line}, col ${column}: ${msg}`);
    errorDetails.push({
      line,
      column,
      msg,
    });
  }
}

function createParser(code, { silent = false } = {}) {
  const chars = new antlr4.InputStream(code);
  const lexer = new sequenceLexer(chars);
  if (silent) {
    lexer.removeErrorListeners();
  }
  const tokens = new antlr4.CommonTokenStream(lexer);
  const parser = new sequenceParser(tokens);
  if (silent) {
    parser.removeErrorListeners();
  }
  return parser;
}

// Memoize error-free parse results so the same code is parsed once even when
// multiple render paths (store, SVG export, CLI) need the tree.
const rootContextCache = new Map();
const ROOT_CONTEXT_CACHE_MAX = 10;

function rootContext(code) {
  const cached = rootContextCache.get(code);
  if (cached) {
    return cached;
  }

  // Two-stage parsing: SLL prediction is an order of magnitude faster than
  // full LL and, when it succeeds, is guaranteed to produce the same parse
  // tree. BailErrorStrategy aborts on the first syntax error so invalid input
  // falls through to the full LL parse below, which keeps error recovery and
  // error reporting behavior unchanged.
  const sllParser = createParser(code, { silent: true });
  sllParser._interp.predictionMode = antlr4.atn.PredictionMode.SLL;
  sllParser._errHandler = new antlr4.error.BailErrorStrategy();
  try {
    const tree = sllParser.prog();
    rootContextCache.set(code, tree);
    if (rootContextCache.size > ROOT_CONTEXT_CACHE_MAX) {
      rootContextCache.delete(rootContextCache.keys().next().value);
    }
    return tree;
  } catch {
    // fall through to full LL parse
  }

  const parser = createParser(code);
  parser.addErrorListener(new SeqErrorListener());
  return parser._syntaxErrors ? null : parser.prog();
}

antlr4.ParserRuleContext.prototype.getFormattedText = function () {
  const code = this.parser.getTokenStream().getText(this.getSourceInterval());
  // remove extra quotes, spaces and new lines
  return formatText(code);
};

// Comment is where users have the most flexibility. The parser should make minimal assumptions about
// the content and the style including change of line, indentation, etc.
antlr4.ParserRuleContext.prototype.getComment = function () {
  let tokenIndex = this.start.tokenIndex;
  let channel = sequenceLexer.channelNames.indexOf("COMMENT_CHANNEL");
  if (this.constructor.name === "BraceBlockContext") {
    tokenIndex = this.stop.tokenIndex;
  }
  let hiddenTokensToLeft = this.parser
    .getTokenStream()
    .getHiddenTokensToLeft(tokenIndex, channel);
  return (
    hiddenTokensToLeft &&
    hiddenTokensToLeft
      .map((t) => t.text.substring(2)) // skip '//'
      .join("\n")
  );
};

export const ProgContext = sequenceParser.ProgContext;
export const RootContext = rootContext;
export const GroupContext = sequenceParser.GroupContext;
export const ParticipantContext = sequenceParser.ParticipantContext;
export const Depth = function (ctx) {
  const childFragmentDetector = ChildFragmentDetector;
  return childFragmentDetector.depth(childFragmentDetector)(ctx);
};
export const Participants = function (ctx) {
  const toCollector = ToCollector;
  return toCollector.getParticipants(ctx);
};

export default {
  RootContext: rootContext,
  ProgContext: sequenceParser.ProgContext,
  GroupContext: sequenceParser.GroupContext,
  ParticipantContext: sequenceParser.ParticipantContext,
  Participants: function (ctx) {
    const toCollector = ToCollector;
    return toCollector.getParticipants(ctx);
  },
  Errors: errors,
  ErrorDetails: errorDetails,
  /**
   * @return {number} how many levels of embedded fragments
   */
  Depth: function (ctx) {
    const childFragmentDetector = ChildFragmentDetector;
    return childFragmentDetector.depth(childFragmentDetector)(ctx);
  },
};
